import { useState, useMemo } from "react";
import useGetResource from "@/hooks/useGetResources";
import useGetMetadata from "@/hooks/useGetMetaData";
import ViewUserProfile from "@/pages/user/profile/viewUser";
import { getMultiSelectStyles } from "@/lib/styles";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from "@mui/material";
import { ArrowUpward, ArrowDownward, Sort } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import PeopleIcon from "@mui/icons-material/People";
import FilterListIcon from "@mui/icons-material/FilterList";
import Select from "react-select";
import { convertUTCToLocal } from "@/lib/utils/dateUtils";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import PageHeader from "@/components/PageHeader";

// Styled Components
const EnhancedTableRow = styled(TableRow)(({ theme }) => ({
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
  cursor: "pointer",
}));

const SearchUser = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const multiSelectStyles = getMultiSelectStyles(theme);

  // State
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedUserGroups, setSelectedUserGroups] = useState([]);
  const [selectedUserTypes, setSelectedUserTypes] = useState([]);
  const [viewProfile, setViewProfile] = useState(false);
  const [viewedUser, setViewedUser] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Metadata Hooks
  const {
    isLoading: isMetadataLoading,
    SitesList,
    userGroupsList,
  } = useGetMetadata();

  const userTypeOptions = [
    { value: "ORG_ADMIN", label: t("searchUser.userTypeOrgAdmin") },
    { value: "ORG_USER", label: t("searchUser.userTypeOrgUser") },
  ];

  // Format select options
  const siteOptions = useMemo(
    () =>
      SitesList?.map((site) => ({ value: site.value, label: site.label })) ||
      [],
    [SitesList]
  );
  const userGroupOptions = useMemo(
    () =>
      userGroupsList?.map((group) => ({
        value: group.value,
        label: group.label,
      })) || [],
    [userGroupsList]
  );

  // Construct query URL
  const queryUrl = useMemo(() => {
    const baseUrl = "/api/v1/users/search";
    const params = new URLSearchParams();
    if (selectedSites.length > 0) {
      params.append(
        "site_ids",
        selectedSites.map((site) => site.value).join(",")
      );
    }
    if (selectedUserGroups.length > 0) {
      params.append(
        "user_group_ids",
        selectedUserGroups.map((group) => group.value).join(",")
      );
    }
    if (selectedUserTypes.length > 0) {
      params.append(
        "user_types",
        selectedUserTypes.map((type) => type.value).join(",")
      );
    }
    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  }, [selectedSites, selectedUserGroups, selectedUserTypes]);

  // Fetch users
  const {
    data: myusers,
    isLoading: myusersloading,
    error: errorMyUsers,
  } = useGetResource(
    ["users", selectedSites, selectedUserGroups, selectedUserTypes],
    queryUrl,
    { enabled: !isMetadataLoading }
  );

  // Deduplicate users
  const users = useMemo(() => {
    if (!myusers) return [];
    const seenIds = new Set();
    return myusers.filter((user) => {
      if (seenIds.has(user.id)) return false;
      seenIds.add(user.id);
      return true;
    });
  }, [myusers]);

  // Sorting logic
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key && prev.direction === "asc") {
        return { key, direction: "desc" };
      }
      return { key, direction: "asc" };
    });
  };

  const sortUsers = (users) => {
    if (!sortConfig.key || !users) return users;

    const sorted = [...users];
    const { key, direction } = sortConfig;

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (key) {
        case "first_name":
          aValue = a.first_name || "";
          bValue = b.first_name || "";
          break;
        case "last_name":
          aValue = a.last_name || "";
          bValue = b.last_name || "";
          break;
        case "username":
          aValue = a.username || "";
          bValue = b.username || "";
          break;
        case "user_type":
          aValue = a.user_type || "";
          bValue = b.user_type || "";
          break;
        case "created_at":
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case "active":
          aValue = a.active ? 1 : 0;
          bValue = b.active ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const sortedUsers = sortUsers(users);

  // Handlers
  const handleRowClick = (user) => {
    setViewedUser(user);
    setViewProfile(true);
  };

  const handleResetFilters = () => {
    setSelectedSites([]);
    setSelectedUserGroups([]);
    setSelectedUserTypes([]);
  };

  // Calculate stats - not used in compact header anymore
  // const activeUsers = users.filter((user) => user.active).length;
  // const userTypesCount = new Set(users.map((user) => user.user_type)).size;

  // Loading and Error States
  if (isMetadataLoading || myusersloading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        borderRadius={2}
      >
        <CircularProgress size={48} aria-label={t("searchUser.loadingAria")} />
        <Typography variant="body1" ml={2} color="text.secondary">
          {t("searchUser.loadingUsers")}
        </Typography>
      </Box>
    );
  }

  if (errorMyUsers) {
    return (
      <Box p={3} borderRadius={2} textAlign="center">
        <Typography variant="h6" color={theme.palette.error.main}>
          {t("searchUser.errorTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("searchUser.errorMessage")}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          {t("searchUser.retryButton")}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: theme.palette.background.default,
      }}
    >
      {/* Compact Enterprise Header */}
      <PageHeader
        icon={<SearchIcon />}
        title={t("searchUser.title")}
        subtitle={t("searchUser.subtitle")}
        stats={[{ label: `${users.length}` }]}
        actions={[
          {
            label: t("searchUser.resetFilters"),
            icon: <FilterListIcon />,
            onClick: handleResetFilters,
            ariaLabel: t("searchUser.resetFiltersAria"),
            show:
              selectedSites.length > 0 ||
              selectedUserGroups.length > 0 ||
              selectedUserTypes.length > 0,
          },
        ]}
        t={t}
        translationPrefix="searchUser"
        compact={true}
      />

      {/* Enhanced Filters Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "flex-start" },
          gap: 2,
          mb: 3,
        }}
      >
        <Accordion
          sx={{
            width: "100%",
            boxShadow: 2,
            borderRadius: 2,
            "&:before": { display: "none" },
          }}
          defaultExpanded
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              minHeight: 56,
              height: 56,
              px: 3,
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FilterListIcon />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {t("searchUser.searchFilters")}
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ px: 3, pb: 3 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {t("searchUser.sitesLabel")}
                </Typography>
                <Select
                  isMulti
                  options={siteOptions}
                  value={selectedSites}
                  onChange={setSelectedSites}
                  placeholder={t("searchUser.sitesPlaceholder")}
                  isLoading={isMetadataLoading}
                  styles={{
                    ...multiSelectStyles,
                    menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                  }}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  aria-label={t("searchUser.sitesAria")}
                />
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {t("searchUser.userGroupsLabel")}
                </Typography>
                <Select
                  isMulti
                  options={userGroupOptions}
                  value={selectedUserGroups}
                  onChange={setSelectedUserGroups}
                  placeholder={t("searchUser.userGroupsPlaceholder")}
                  isLoading={isMetadataLoading}
                  styles={{
                    ...multiSelectStyles,
                    menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                  }}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  aria-label={t("searchUser.userGroupsAria")}
                />
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {t("searchUser.userTypesLabel")}
                </Typography>
                <Select
                  isMulti
                  options={userTypeOptions.map((option) => ({
                    ...option,
                    label: t(option.label),
                  }))}
                  value={selectedUserTypes}
                  onChange={setSelectedUserTypes}
                  placeholder={t("searchUser.userTypesPlaceholder")}
                  styles={{
                    ...multiSelectStyles,
                    menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                  }}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  aria-label={t("searchUser.userTypesAria")}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      {sortedUsers.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 3,
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: 64,
              color: theme.palette.text.disabled,
              mb: 2,
            }}
          >
            🔍
          </Typography>
          <Typography
            variant="h5"
            sx={{
              mb: 1,
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            {t("searchUser.noUsersTitle")}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {t("searchUser.noUsersFound")}
          </Typography>
        </Box>
      )}

      {sortedUsers.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            overflow: "hidden",
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Results Header */}
          <Box
            sx={{
              p: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <PeopleIcon
                sx={{ color: theme.palette.primary.main, fontSize: 28 }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {t("searchUser.searchResults")}
              </Typography>
              <Chip
                label={`${sortedUsers.length} ${t("searchUser.users")}`}
                color="primary"
                size="small"
                sx={{
                  fontWeight: 600,
                  ml: 1,
                }}
              />
            </Box>
          </Box>

          <TableContainer sx={{ maxHeight: "600px" }}>
            <Table stickyHeader aria-label={t("searchUser.tableAria")}>
              <TableHead>
                <TableRow
                  sx={{
                    "& .MuiTableCell-head": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.background.paper, 0.8)
                          : theme.palette.grey[50],
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      borderBottom: `2px solid ${theme.palette.divider}`,
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {t("searchUser.firstNameLabel")}
                      <IconButton
                        size="small"
                        onClick={() => handleSort("first_name")}
                        sx={{
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        aria-label={t("searchUser.sortFirstNameAria", {
                          direction:
                            sortConfig.key === "first_name" &&
                            sortConfig.direction === "asc"
                              ? t("searchUser.ascendingAria")
                              : t("searchUser.descendingAria"),
                        })}
                      >
                        {sortConfig.key === "first_name" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUpward fontSize="small" />
                          ) : (
                            <ArrowDownward fontSize="small" />
                          )
                        ) : (
                          <Sort fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {t("searchUser.lastNameLabel")}
                      <IconButton
                        size="small"
                        onClick={() => handleSort("last_name")}
                        sx={{
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        aria-label={t("searchUser.sortLastNameAria", {
                          direction:
                            sortConfig.key === "last_name" &&
                            sortConfig.direction === "asc"
                              ? t("searchUser.ascendingAria")
                              : t("searchUser.descendingAria"),
                        })}
                      >
                        {sortConfig.key === "last_name" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUpward fontSize="small" />
                          ) : (
                            <ArrowDownward fontSize="small" />
                          )
                        ) : (
                          <Sort fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {t("searchUser.usernameLabel")}
                      <IconButton
                        size="small"
                        onClick={() => handleSort("username")}
                        sx={{
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        aria-label={t("searchUser.sortUsernameAria", {
                          direction:
                            sortConfig.key === "username" &&
                            sortConfig.direction === "asc"
                              ? t("searchUser.ascendingAria")
                              : t("searchUser.descendingAria"),
                        })}
                      >
                        {sortConfig.key === "username" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUpward fontSize="small" />
                          ) : (
                            <ArrowDownward fontSize="small" />
                          )
                        ) : (
                          <Sort fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {t("searchUser.userTypeLabel")}
                      <IconButton
                        size="small"
                        onClick={() => handleSort("user_type")}
                        sx={{
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        aria-label={t("searchUser.sortUserTypeAria", {
                          direction:
                            sortConfig.key === "user_type" &&
                            sortConfig.direction === "asc"
                              ? t("searchUser.ascendingAria")
                              : t("searchUser.descendingAria"),
                        })}
                      >
                        {sortConfig.key === "user_type" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUpward fontSize="small" />
                          ) : (
                            <ArrowDownward fontSize="small" />
                          )
                        ) : (
                          <Sort fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {t("searchUser.createdAtLabel")}
                      <IconButton
                        size="small"
                        onClick={() => handleSort("created_at")}
                        sx={{
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        aria-label={t("searchUser.sortCreatedAtAria", {
                          direction:
                            sortConfig.key === "created_at" &&
                            sortConfig.direction === "asc"
                              ? t("searchUser.ascendingAria")
                              : t("searchUser.descendingAria"),
                        })}
                      >
                        {sortConfig.key === "created_at" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUpward fontSize="small" />
                          ) : (
                            <ArrowDownward fontSize="small" />
                          )
                        ) : (
                          <Sort fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {t("searchUser.activeLabel")}
                      <IconButton
                        size="small"
                        onClick={() => handleSort("active")}
                        sx={{
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        aria-label={t("searchUser.sortActiveAria", {
                          direction:
                            sortConfig.key === "active" &&
                            sortConfig.direction === "asc"
                              ? t("searchUser.ascendingAria")
                              : t("searchUser.descendingAria"),
                        })}
                      >
                        {sortConfig.key === "active" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUpward fontSize="small" />
                          ) : (
                            <ArrowDownward fontSize="small" />
                          )
                        ) : (
                          <Sort fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedUsers.map((user) => (
                  <EnhancedTableRow
                    key={user.id}
                    onClick={() => handleRowClick(user)}
                    aria-label={t("searchUser.viewProfileAria", {
                      username:
                        user.username || t("searchUser.unknownUsername"),
                    })}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      {user.first_name || t("searchUser.naText")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {user.last_name || t("searchUser.naText")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {user.username || t("searchUser.naText")}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          t(`searchUser.userType${user.user_type}`) ||
                          t("searchUser.naText")
                        }
                        color={
                          user.user_type === "ORG_ADMIN" ? "primary" : "default"
                        }
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: theme.palette.text.secondary }}>
                      {convertUTCToLocal(user.created_at)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          user.active
                            ? t("searchUser.activeChip")
                            : t("searchUser.inactiveChip")
                        }
                        color={user.active ? "success" : "default"}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          ...(user.active && {
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            borderColor: theme.palette.success.main,
                            color: theme.palette.success.main,
                          }),
                        }}
                      />
                    </TableCell>
                  </EnhancedTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {viewProfile && (
        <ViewUserProfile
          user={viewedUser}
          open={viewProfile}
          onClose={() => setViewProfile(false)}
        />
      )}
    </Box>
  );
};

export default SearchUser;
